import { LearningModesPanel } from './LearningModesPanel';
import { LearningCohortsPanel } from './LearningCohortsPanel';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { darkTheme } from '../../lib/tokens';
import {
  fetchAcquisition,
  fetchOnboardingFunnel,
  fetchOverview,
  fetchPlanAdviceMix,
  fetchStornoVergleich,
  fetchLektionsSuche,
  fetchSchoolAttendance,
  fetchSubscriptionFunnel,
  fetchTimeseries,
  fetchTopTopics,
} from './api';
import type {
  AcquisitionBreakdown,
  OnboardingFunnelStep,
  OnboardingLabeledCount,
  OnboardingPlatform,
  SchoolAttendanceBreakdown,
  TimeseriesMetric,
  TopTopic,
} from './types';
import { useAppContextStore } from '../app/appContextStore';
import { ALL_APPS, DEFAULT_APP_ID, appLabel } from '../../lib/appConstants';
import { SCHOOL_SYNC_LABELS, SCHEDULE_SOURCE_LABELS } from '../../lib/schoolLabels';

export function AnalyticsPage(): JSX.Element {
  const [days, setDays] = useState<7 | 30 | 90>(30);

  // KPIs pro App (globaler Picker; „Alle Apps" → currivium). Aktivität/Entitlements
  // scopen darauf; signups/Gesamt-Nutzer sind app-übergreifend (Backend).
  const ctxApp = useAppContextStore((s) => s.appId);
  const app = ctxApp === ALL_APPS ? DEFAULT_APP_ID : ctxApp;

  const overview = useQuery({
    queryKey: ['analytics', 'overview', app],
    queryFn: () => fetchOverview(app),
    refetchInterval: 5 * 60_000,
  });

  const funnel = useQuery({
    queryKey: ['analytics', 'funnel', app],
    queryFn: () => fetchSubscriptionFunnel(app),
    refetchInterval: 10 * 60_000,
  });

  const topTopics = useQuery({
    queryKey: ['analytics', 'top-topics', app, days],
    queryFn: () => fetchTopTopics(app, days, 10),
    refetchInterval: 10 * 60_000,
  });

  const acquisition = useQuery({
    queryKey: ['analytics', 'acquisition', app, days],
    queryFn: () => fetchAcquisition(app, days),
    refetchInterval: 10 * 60_000,
  });

  const schoolAttendance = useQuery({
    queryKey: ['analytics', 'school-attendance', app],
    queryFn: () => fetchSchoolAttendance(app),
    refetchInterval: 10 * 60_000,
  });

  return (
    <div className="mx-auto max-w-[1400px]">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-text-primary">
            Eigene KPIs
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-normal"
              style={{
                background: darkTheme.colors.accentSubtle,
                color: darkTheme.colors.accentLight,
                border: `1px solid ${darkTheme.colors.accent}`,
              }}
              title="Oben im App-Picker wählbar — scopt Aktivität, Themen & Umsatz"
            >
              {appLabel(app)}
            </span>
          </h1>
          <p className="mt-1 text-xs text-text-tertiary">
            Aus eigener DB. 5-min-Cache. Komplementär zu PostHog (Funnels/Retention).
            Signups &amp; Gesamt-Nutzer sind app-übergreifend (eine Identität pro Person).
          </p>
        </div>
        <RangeSwitch value={days} onChange={setDays} />
      </header>

      <LearningCohortsPanel app={app} />
      <LearningModesPanel app={app} />

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Aktivität
          <span className="ml-2 normal-case text-text-tertiary">
            · feste Fenster, unabhängig vom Zeitraum-Filter oben
          </span>
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <KpiTile label="DAU" value={overview.data?.dau} hint="Aktiv 24 h" />
          <KpiTile label="WAU" value={overview.data?.wau} hint="Aktiv 7 Tage" />
          <KpiTile label="MAU" value={overview.data?.mau} hint="Aktiv 30 Tage" />
          <KpiTile
            label="Aktive Konten"
            value={overview.data?.totalUsersActive}
            hint="nicht-suspended/-deleted"
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Wachstum
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <KpiTile label="Signups heute" value={overview.data?.signupsToday} />
          <KpiTile label="Signups 7 Tage" value={overview.data?.signupsLast7d} />
          <KpiTile label="Signups 30 Tage" value={overview.data?.signupsLast30d} />
          <KpiTile
            label="Conversion (kumuliert)"
            value={
              funnel.data ? `${funnel.data.conversionRatePct.toFixed(1)}%` : undefined
            }
            hint="paid / total Users"
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Subscriptions
          <span className="ml-2 normal-case text-text-tertiary">
            · aktueller Bestand, unabhängig vom Zeitraum-Filter oben
          </span>
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <KpiTile
            label="Pro aktiv"
            value={overview.data?.paidSubscribersActive}
            hint="bezahlt + nicht abgelaufen, jetzt"
          />
          <KpiTile label="Trial aktiv" value={overview.data?.trialActive} hint="jetzt" />
          <KpiTile
            label="Pro je gestartet"
            value={funnel.data?.paidEverStarted}
            hint="kumuliert seit Start"
          />
          <KpiTile
            label="Trial je gestartet"
            value={funnel.data?.trialEverStarted}
          />
        </div>
      </section>

      <PlanAdviceMixSection app={app} days={days} />

      <LektionsSucheSection app={app} days={days} />

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Lernen
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <KpiTile label="Lektionen heute" value={overview.data?.lessonsCompletedToday} />
          <KpiTile
            label="Lektionen 30 Tage"
            value={overview.data?.lessonsCompletedLast30d}
          />
          <KpiTile
            label="Voice-Sessions heute"
            value={overview.data?.voiceTutorSessionsToday}
          />
        </div>
      </section>

      <SchoolAttendanceSection data={schoolAttendance.data} loading={schoolAttendance.isLoading} />

      <OnboardingFunnelSection app={app} days={days} />

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Akquisition
          <span className="ml-2 normal-case text-text-tertiary">· letzte {days} Tage</span>
        </h2>
        <AcquisitionCard data={acquisition.data} loading={acquisition.isLoading} />
      </section>

      <section className="mb-8 grid gap-4 lg:grid-cols-2">
        <ChartCard title="Signups" metric="signups" days={days} app={app} />
        <ChartCard title="Lektionen abgeschlossen" metric="lessons_completed" days={days} app={app} />
        <ChartCard title="Quiz-Sessions" metric="quizzes_submitted" days={days} app={app} />
        <ChartCard title="Voice-Tutor-Sessions" metric="voice_sessions" days={days} app={app} />
      </section>

      <section className="mb-8 grid gap-4 lg:grid-cols-2">
        <ChartCard title="Erstmals Pro" metric="paid_subscriptions" days={days} app={app} />
        <TopTopicsCard topics={topTopics.data ?? []} loading={topTopics.isLoading} days={days} />
      </section>

      {overview.data && (
        <p className="text-[11px] text-text-tertiary">
          Letzter Refresh: {formatRelative(overview.data.generatedAt)} · Cache 5 min
        </p>
      )}
    </div>
  );
}

function RangeSwitch({
  value,
  onChange,
}: {
  value: 7 | 30 | 90;
  onChange: (v: 7 | 30 | 90) => void;
}): JSX.Element {
  const opts: (7 | 30 | 90)[] = [7, 30, 90];
  return (
    <div className="flex gap-1.5">
      {opts.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          className="rounded-md px-3 py-1 text-xs"
          style={{
            background: d === value ? darkTheme.colors.accentSubtle : 'transparent',
            color: d === value ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
            border: `1px solid ${d === value ? darkTheme.colors.accent : darkTheme.colors.borderSubtle}`,
          }}
        >
          {d} Tage
        </button>
      ))}
    </div>
  );
}

/**
 * Der Stolperdraht der Tarif-Beratung (CUR-544, AC aus AB-7).
 *
 * Steht bewusst NEBEN den Subscriptions und nicht in einem Anhang: Er ist die
 * einzige Kennzahl, die sagt, ob der Tutor beraet oder verkauft, und eine
 * Kennzahl, die man suchen muss, wird nicht gelesen.
 *
 * Die Anzeige zeigt den Anteil NIE nackt. `stolperdraht` traegt die Vorbehalte
 * in fester Reihenfolge (zu wenig Daten -> kaputter Einordner -> Schwelle), und
 * der mittlere ist der, den man beim Bauen uebersieht: Ohne ihn waere die Zahl
 * selbstbestaetigend, weil eine schlechtere Einordnung weniger zaehlbare
 * Kaufempfehlungen und damit einen besser aussehenden Anteil erzeugt. Deshalb
 * steht `unbekanntProzent` als eigene Kachel daneben — sie macht den Zaehler
 * pruefbar statt ihn zu behaupten.
 */
function PlanAdviceMixSection({ app, days }: { app: string; days: number }): JSX.Element {
  const mix = useQuery({
    queryKey: ['analytics', 'plan-advice-mix', app, days],
    queryFn: () => fetchPlanAdviceMix(app, days),
    refetchInterval: 10 * 60_000,
  });

  const d = mix.data;
  // Farbe folgt dem Stand, nicht der Zahl: „viel Prozent" ist hier nicht
  // automatisch gut, und „wenig" nicht automatisch schlecht — bei zu duenner
  // Datenlage ist beides bedeutungslos.
  const farbe =
    d?.stolperdraht === 'ausgeloest'
      ? darkTheme.colors.danger
      : d?.stolperdraht === 'ok'
        ? darkTheme.colors.success
        : darkTheme.colors.textTertiary;

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Tarif-Beratung — raet der Tutor auch ab?
        <span className="ml-2 normal-case text-text-tertiary">· letzte {days} Tage</span>
      </h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          className="rounded-md border p-4"
          style={{ background: darkTheme.colors.bgSurface, borderColor: farbe }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            Nicht gekauft
          </div>
          <div className="mt-1 text-2xl font-bold tabular" style={{ color: farbe }}>
            {d === undefined ? '—' : `${d.ohneKaufProzent} %`}
          </div>
          <div className="mt-1 text-[10px] text-text-tertiary">
            Schwelle 15 % · {d?.ohneKauf ?? '—'} von {d?.empfehlungen ?? '—'}
          </div>
        </div>
        <KpiTile
          label="Beratungen"
          value={d?.beratungen}
          hint="Gespraeche mit Tarif-Nachschlag"
        />
        <KpiTile
          label="Empfehlungen"
          value={d?.empfehlungen}
          hint="Turns mit eingeordneter Empfehlung"
        />
        <KpiTile
          label="Nicht einordenbar"
          value={d === undefined ? undefined : `${d.unbekanntProzent} %`}
          hint="macht den Zaehler pruefbar"
        />
      </div>

      {d && (
        <p className="mt-2 text-[11px] text-text-tertiary">
          {d.hinweis}
        </p>
      )}

      {d && d.verteilung.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {d.verteilung.map((v) => (
            <span
              key={v.empfehlung}
              className="rounded-full px-2 py-0.5 text-[11px]"
              style={{
                background: darkTheme.colors.bgSurface,
                border: `1px solid ${darkTheme.colors.borderSubtle}`,
                color: darkTheme.colors.textSecondary,
              }}
            >
              {v.empfehlung}: {v.anzahl.toLocaleString('de-DE')}
            </span>
          ))}
        </div>
      )}

      <StornoVergleichZeile app={app} days={days} />

      <p className="mt-2 text-[10px] text-text-tertiary">
        Gezaehlt werden <strong>Empfehlungen, nicht Kaeufe</strong>. „Ich habe zum Bleiben
        geraten" und „ich habe zum Kauf geraten und wurde ignoriert" sehen an der Kasse gleich
        aus und sagen ueber die Ehrlichkeit das Gegenteil. Faellt der Anteil unter die
        Schwelle, wird die Haltung geprueft — nicht die Konversion gefeiert.
      </p>
    </section>
  );
}

/**
 * Der Storno-Vergleich (CUR-544, letztes AC aus AB-7).
 *
 * Steht als Zeile IN der Beratungs-Sektion und nicht als eigener Block: Er
 * beantwortet dieselbe Frage wie der Stolperdraht darueber, nur von der anderen
 * Seite — dort „raet der Tutor auch ab?", hier „und hat es dem Lernenden
 * genuetzt?". Getrennt gerendert waeren es zwei Kennzahlen; nebeneinander ist
 * es eine Aussage.
 */
function StornoVergleichZeile({ app, days }: { app: string; days: number }): JSX.Element | null {
  const q = useQuery({
    queryKey: ['analytics', 'storno-vergleich', app, days],
    queryFn: () => fetchStornoVergleich(app, days),
    refetchInterval: 10 * 60_000,
  });
  const d = q.data;
  if (!d) return null;

  const farbe =
    d.stand === 'auffaellig'
      ? darkTheme.colors.danger
      : d.stand === 'ok'
        ? darkTheme.colors.success
        : darkTheme.colors.textTertiary;

  return (
    <div
      className="mt-3 rounded-md border p-3"
      style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          Storno nach Beratung vs. Paywall
        </span>
        <span className="text-lg font-bold tabular" style={{ color: farbe }}>
          {d.nachBeratung.quoteProzent} % vs. {d.ueberPaywall.quoteProzent} %
        </span>
        <span className="text-[11px] text-text-tertiary">
          {d.nachBeratung.kaeufe} bzw. {d.ueberPaywall.kaeufe} Kaeufe, mindestens{' '}
          {d.reifezeitTage} Tage alt
        </span>
      </div>
      <p className="mt-1 text-[11px] text-text-tertiary">{d.hinweis}</p>
    </div>
  );
}

/**
 * Guete der Lektionssuche (CUR-985).
 *
 * Die Kachel beantwortet eine Frage, die vorher nur per SSH und psql zu
 * beantworten war: Wie oft greift die Suche des Tutors daneben?
 *
 * Sie faerbt nach dem STAND, nicht nach der Zahl — und bei duenner Datenlage
 * gar nicht. Der Tutor-Verkehr lag Anfang September bei rund acht Chats am Tag;
 * eine Kachel, die aus zwoelf Suchen „25 % ohne Treffer" macht, sieht nach
 * Messung aus und ist schaedlicher als keine. Unterhalb der Mindestzahl stehen
 * die Werte deshalb ausdruecklich als Rohwerte da, nicht als Befund.
 */
function LektionsSucheSection({ app, days }: { app: string; days: number }): JSX.Element {
  const q = useQuery({
    queryKey: ['analytics', 'lektionssuche', app, days],
    queryFn: () => fetchLektionsSuche(app, days),
    refetchInterval: 10 * 60_000,
  });

  const d = q.data;
  const farbe =
    d?.stand === 'auffaellig'
      ? darkTheme.colors.danger
      : d?.stand === 'ok'
        ? darkTheme.colors.success
        : darkTheme.colors.textTertiary;

  const wert = (prozent: number | undefined): string =>
    d === undefined || d.stand === 'zu_wenig_daten' ? '—' : `${prozent} %`;

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Lektionssuche des Tutors — findet sie, was gemeint ist?
        <span className="ml-2 normal-case text-text-tertiary">· letzte {days} Tage</span>
      </h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div
          className="rounded-md border p-4"
          style={{ background: darkTheme.colors.bgSurface, borderColor: farbe }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            Ohne Treffer
          </div>
          <div className="mt-1 text-2xl font-bold tabular" style={{ color: farbe }}>
            {wert(d?.ohneTrefferProzent)}
          </div>
          <div className="mt-1 text-[10px] text-text-tertiary">
            {d?.ohneTreffer ?? '—'} von {d?.suchen ?? '—'} Suchen
          </div>
        </div>
        <KpiTile
          label="Mit Rueckfall"
          value={wert(d?.mitRueckfallProzent)}
          hint="Erster Anlauf leer, Wiederholung mit dem trennschaerfsten Token"
        />
        <KpiTile
          label="Nur im Volltext"
          value={wert(d?.nurVolltextProzent)}
          hint="Oberster Treffer stand nicht in Titel oder Schluesselbegriffen"
        />
        <KpiTile
          label="Suchen"
          value={d?.suchen}
          hint={`Mindestens ${d?.mindestFaelle ?? 100} fuer eine Aussage`}
        />
      </div>
      {d?.hinweis !== undefined && (
        <p className="mt-2 text-[11px] text-text-tertiary">{d.hinweis}</p>
      )}
      <p className="mt-2 text-[10px] text-text-tertiary">
        Der <strong>Wortlaut</strong> der Suchanfragen steht hier nicht — er bleibt im Log und
        verfaellt dort nach rund 25 Stunden. Diese Kachel sagt, WIE GROSS das Problem ist;
        welcher Art es ist (Kompositum, Beugung, Umgangssprache), sagt eine Stichprobe aus dem
        Log. Siehe CUR-974.
      </p>
    </section>
  );
}

/**
 * Prozentwert als Kachel-Text, „—" wenn der Nenner fehlt oder 0 ist.
 *
 * `undefined` ist hier ein echter Fall, kein Typ-Pedantismus: Der Funnel-
 * Endpoint cacht 5 Minuten, und beim rollierenden Deploy kann ein Payload
 * ohne die neueren Felder ankommen (CUR-1410, Review B6). Die Kachel zeigt
 * dann „—" statt die Seite zu werfen.
 */
function pct(zaehler: number | undefined, nenner: number | undefined): string {
  if (typeof zaehler !== 'number' || typeof nenner !== 'number' || nenner <= 0) return '—';
  return `${((zaehler / nenner) * 100).toFixed(1)} %`;
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string | undefined;
  hint?: string;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular text-text-primary">
        {value === undefined ? '—' : typeof value === 'number' ? value.toLocaleString('de-DE') : value}
      </div>
      {hint && <div className="mt-1 text-[10px] text-text-tertiary">{hint}</div>}
    </div>
  );
}

function ChartCard({
  title,
  metric,
  days,
  app,
}: {
  title: string;
  metric: TimeseriesMetric;
  days: number;
  app: string;
}): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', 'timeseries', metric, days, app],
    queryFn: () => fetchTimeseries(metric, app, days),
    refetchInterval: 10 * 60_000,
  });

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {data && (
          <span className="text-[11px] text-text-tertiary">
            Σ {data.reduce((acc, p) => acc + p.count, 0).toLocaleString('de-DE')}
          </span>
        )}
      </div>
      {isLoading && <div className="h-[180px] animate-pulse rounded bg-bg-elevated" />}
      {error && (
        <div
          className="rounded-md p-3 text-xs"
          style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
        >
          Fehler: {error instanceof Error ? error.message : 'unbekannt'}
        </div>
      )}
      {data && data.length > 0 && (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={darkTheme.colors.borderSubtle} />
            <XAxis
              dataKey="day"
              stroke={darkTheme.colors.textTertiary}
              fontSize={10}
              tickFormatter={(v) => v.slice(5)}
            />
            <YAxis stroke={darkTheme.colors.textTertiary} fontSize={10} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: darkTheme.colors.bgElevated,
                border: `1px solid ${darkTheme.colors.borderSubtle}`,
                fontSize: 12,
              }}
              labelStyle={{ color: darkTheme.colors.textSecondary }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line
              type="monotone"
              dataKey="count"
              name={title}
              stroke={darkTheme.colors.accent}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function TopTopicsCard({
  topics,
  loading,
  days,
}: {
  topics: TopTopic[];
  loading: boolean;
  days: number;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <h3 className="mb-3 text-sm font-semibold text-text-primary">
        Top-Topics nach abgeschlossenen Lektionen
        <span className="ml-2 font-normal text-text-tertiary">· letzte {days} Tage</span>
      </h3>
      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-5 animate-pulse rounded bg-bg-elevated" />
          ))}
        </div>
      )}
      {!loading && topics.length === 0 && (
        <p className="text-xs text-text-tertiary">Noch keine Daten.</p>
      )}
      {!loading && topics.length > 0 && (
        <ol className="space-y-2 text-sm">
          {topics.map((t, i) => {
            const max = topics[0]!.completedCount;
            const pct = max > 0 ? (t.completedCount / max) * 100 : 0;
            return (
              <li key={t.topicId}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-text-secondary">
                    {i + 1}. {t.title}
                  </span>
                  <span className="tabular text-text-primary">
                    {t.completedCount.toLocaleString('de-DE')}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-bg-elevated">
                  <div
                    className="h-full"
                    style={{ width: `${pct}%`, background: darkTheme.colors.accent }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

const ACQUISITION_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
  FRIENDS_FAMILY: 'Freunde & Familie',
  APP_STORE: 'App Store',
  GOOGLE_SEARCH: 'Google-Suche',
  SCHOOL: 'HP-Schule',
  PODCAST: 'Podcast',
  OTHER: 'Sonstiges',
  UNKNOWN: 'Nicht angegeben',
};

function AcquisitionCard({
  data,
  loading,
}: {
  data: AcquisitionBreakdown | undefined;
  loading: boolean;
}): JSX.Element {
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          Woher kennen die Nutzer Currivium?
        </h3>
        {data && (
          <span className="text-[11px] text-text-tertiary">
            {data.answered.toLocaleString('de-DE')} / {data.total.toLocaleString('de-DE')}{' '}
            beantwortet
          </span>
        )}
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-5 animate-pulse rounded bg-bg-elevated" />
          ))}
        </div>
      )}

      {!loading && (!data || data.buckets.length === 0) && (
        <p className="text-xs text-text-tertiary">Noch keine Daten.</p>
      )}

      {!loading && data && data.buckets.length > 0 && (
        <ol className="space-y-2 text-sm">
          {data.buckets.map((b) => {
            const max = data.buckets[0]!.count;
            const barPct = max > 0 ? (b.count / max) * 100 : 0;
            const muted = b.source === 'UNKNOWN';
            return (
              <li key={b.source}>
                <div className="flex items-baseline justify-between gap-2">
                  <span style={{ color: muted ? darkTheme.colors.textTertiary : darkTheme.colors.textSecondary }}>
                    {ACQUISITION_LABELS[b.source] ?? b.source}
                  </span>
                  <span className="tabular text-text-primary">
                    {b.count.toLocaleString('de-DE')}{' '}
                    <span className="text-[11px] text-text-tertiary">({b.pct.toFixed(1)} %)</span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-bg-elevated">
                  <div
                    className="h-full"
                    style={{
                      width: `${barPct}%`,
                      background: muted ? darkTheme.colors.borderSubtle : darkTheme.colors.accent,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ── Schule & Stundenplan ───────────────────────────────────

function SchoolAttendanceSection({
  data,
  loading,
}: {
  data: SchoolAttendanceBreakdown | undefined;
  loading: boolean;
}): JSX.Element {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Schule
        <span className="ml-2 normal-case text-text-tertiary">
          · aktueller Bestand, unabhängig vom Zeitraum-Filter oben
        </span>
      </h2>
      {loading && !data && <div className="h-[140px] animate-pulse rounded-md bg-bg-elevated" />}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            <KpiTile
              label="Besucht Schule"
              value={`${data.attendsSchoolCount.toLocaleString('de-DE')} (${data.attendsSchoolPct.toFixed(1)} %)`}
              hint={`von ${data.totalProfiles.toLocaleString('de-DE')} Nutzern`}
            />
            <KpiTile
              label="Stundenplan verknüpft"
              value={`${data.scheduleLinkedCount.toLocaleString('de-DE')} (${data.scheduleLinkedPct.toFixed(1)} %)`}
              hint={`von ${data.totalProfiles.toLocaleString('de-DE')} Nutzern`}
            />
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <BreakdownCard
              title="Sync-Modus (unter Schule-Besuchern)"
              items={data.syncModeBreakdown}
              labels={SCHOOL_SYNC_LABELS}
            />
            <BreakdownCard
              title="Stundenplan-Quelle"
              items={data.scheduleSourceBreakdown}
              labels={SCHEDULE_SOURCE_LABELS}
            />
          </div>
        </>
      )}
    </section>
  );
}

// ── Onboarding-v2-Funnel (M5) ─────────────────────────────

const ONB_STEP_LABELS: Record<string, string> = {
  PersonaPick: 'Persona-Wahl',
  AcquisitionSource: 'Akquise-Quelle',
  PriorKnowledge: 'Vorwissen',
  PrepPath: 'Weg-Weiche',
  Rhythm: 'Lern-Rhythmus',
  FirstWeekInterstitial: 'Erste-Woche-Vorschau',
  NotificationPermission: 'Benachrichtigungen',
  Goal: 'Prüfungsziel',
  PlanReady: 'Plan-Vorschau',
  // Legacy-Schritte (historische Events vor dem M3-Neuschnitt):
  DailyMinutes: 'Tägl. Minuten (alt)',
  LearningDays: 'Lerntage (alt)',
  SessionLength: 'Einheit-Länge (alt)',
  PreferredTime: 'Tageszeit (alt)',
  School: 'Schule (alt)',
  Location: 'Ort (alt)',
};

const ONB_VARIANT_LABELS: Record<string, string> = {
  school: 'Schule (Coverage)',
  target: 'Zieltermin',
  flexible: 'Flexibel',
  unbekannt: 'Unbekannt',
};

const ONB_SOURCE_LABELS: Record<string, string> = {
  onboarding: 'Direkt im Flow',
  deferred: 'Später abgeschlossen',
  quick_start: 'Direkt loslegen',
  unbekannt: 'Unbekannt',
};

const ONB_PLATFORMS: { value: OnboardingPlatform; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'web', label: 'Web' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

function OnboardingFunnelSection({ app, days }: { app: string; days: number }): JSX.Element {
  const [platform, setPlatform] = useState<OnboardingPlatform>('all');
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', 'onboarding-funnel', app, days, platform],
    queryFn: () => fetchOnboardingFunnel(app, days, platform),
    refetchInterval: 5 * 60_000,
  });

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Onboarding-Funnel
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case"
            style={{
              background: darkTheme.colors.accentSubtle,
              color: darkTheme.colors.accentLight,
              border: `1px solid ${darkTheme.colors.accent}`,
            }}
          >
            v2 · first-party
          </span>
        </h2>
        <div className="flex gap-1.5">
          {ONB_PLATFORMS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPlatform(p.value)}
              className="rounded-md px-2.5 py-1 text-xs"
              style={{
                background: p.value === platform ? darkTheme.colors.accentSubtle : 'transparent',
                color:
                  p.value === platform
                    ? darkTheme.colors.accentLight
                    : darkTheme.colors.textSecondary,
                border: `1px solid ${p.value === platform ? darkTheme.colors.accent : darkTheme.colors.borderSubtle}`,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          className="rounded-md p-3 text-xs"
          style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
        >
          Fehler: {error instanceof Error ? error.message : 'unbekannt'}
        </div>
      )}

      {isLoading && !data && (
        <div className="h-[220px] animate-pulse rounded-md bg-bg-elevated" />
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            <KpiTile label="Einstieg" value={data.started} hint="onboarding_started" />
            <KpiTile
              label="Registrierung"
              value={data.registrationCompleted}
              hint={`${data.startedToRegistrationPct.toFixed(1)} % ab Einstieg`}
            />
            <KpiTile
              label="Abschluss"
              value={data.completed}
              hint={`${data.startedToCompletedPct.toFixed(1)} % ab Einstieg`}
            />
            <KpiTile
              label="Später abschließen"
              value={data.deferred}
              hint="Flow → Registrierung verschoben"
            />
            <KpiTile
              label="Prognose gesehen"
              value={data.projectionViewed}
              hint="plan_projection_viewed"
            />
            {/*
              Optional-Chaining auf `pathChosen`/`users`, obwohl der Typ sie
              als Pflicht führt: Der Endpoint cacht 5 Minuten, und nach einem
              Deploy kann bis dahin ein Payload des alten Builds ohne diese
              Felder kommen (Review B6). Dann „—" statt einer geworfenen Seite.
            */}
            <KpiTile
              label="Weiche: direkt losgelegt"
              value={pct(data.pathChosen?.quick_start, data.welcomeViewed)}
              hint={
                `${data.pathChosen?.quick_start ?? '—'} direkt · ` +
                `${data.pathChosen?.personalize ?? '—'} personalisiert · ` +
                `${data.welcomeViewed ?? '—'} Aufrufe`
              }
            />
            <KpiTile
              label="Nachholquote (7 Tage)"
              // Nenner ist die Kohorte, die im gewählten Zeitraum 7 Tage alt
              // geworden ist — nicht alle Quick-Start-Konten des Zeitraums:
              // wer gestern angelegt wurde, kann keine sieben Tage hinter sich
              // haben und würde die Quote drücken, ohne etwas zu bedeuten.
              // `quickStart` (im Zeitraum angelegt) ist deshalb eine andere
              // Population als `eligible7d` und taugt nicht als Nenner.
              value={pct(data.users?.personalizedWithin7d, data.users?.eligible7d)}
              hint={
                data.users === undefined
                  ? 'Konten-Daten fehlen (alter Cache-Stand)'
                  : data.users.eligible7d === 0
                    ? `im Zeitraum ist noch kein Konto 7 Tage alt geworden (${data.users.quickStart} angelegt)`
                    : `${data.users.personalizedWithin7d} von ${data.users.eligible7d} Konten, die im Zeitraum 7 Tage alt wurden · ` +
                      `14 T: ${data.users.personalizedWithin14d}/${data.users.eligible14d}` +
                      (data.users.medianDaysToPersonalize === null
                        ? ''
                        : ` · Median ${data.users.medianDaysToPersonalize} T`)
              }
            />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <StepDropoffCard steps={data.steps} started={data.started} />
            <div className="grid gap-4">
              <BreakdownCard
                title="Prognose-Variante (Prüfungsziel)"
                items={data.projectionByVariant}
                labels={ONB_VARIANT_LABELS}
              />
              <BreakdownCard
                title="Registrierungs-Quelle"
                items={data.registrationBySource}
                labels={ONB_SOURCE_LABELS}
              />
              <BreakdownCard
                title="„Später abschließen“ nach Schritt"
                items={data.deferredByStep}
                labels={ONB_STEP_LABELS}
              />
              <ResumeMiniFunnel shown={data.resumeShown} clicked={data.resumeClicked} />
            </div>
          </div>

          <p className="mt-2 text-[11px] leading-relaxed text-text-tertiary">
            Ereigniszählung, keine eindeutigen Nutzer (pre-Auth ohne Session-Key) — Rücksprünge
            zählen mehrfach; als Richtung lesen, nicht als Kohorte. Einstieg = onboarding_started
            (app_first_open ist PostHog-only). Seit CUR-1410 ordnet der Sink jedes Ereignis seiner
            App zu; Zeilen von davor liegen alle unter currivium, ein Fenster über den Deploy hinweg
            mischt daher zwei Zählweisen. Die Nachholquote kommt aus den Profilen, nicht aus
            Ereignissen — sie kennt den Plattform-Filter nicht. Fenster: {days} Tage.
          </p>
        </>
      )}
    </section>
  );
}

function StepDropoffCard({
  steps,
  started,
}: {
  steps: OnboardingFunnelStep[];
  started: number;
}): JSX.Element {
  // Bezugsgröße: Einstieg (onboarding_started); Fallback erster Schritt.
  const base = started > 0 ? started : steps[0]?.count ?? 0;
  return (
    <div
      className="rounded-md border p-4"
      style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
    >
      <h3 className="mb-3 text-sm font-semibold text-text-primary">Schritt für Schritt (Drop-off)</h3>
      {steps.length === 0 && <p className="text-xs text-text-tertiary">Noch keine Daten.</p>}
      {steps.length > 0 && (
        <ol className="space-y-2.5 text-sm">
          {steps.map((s, i) => {
            const denom = base > 0 ? base : 1;
            const pctOfStart = Math.round((s.count / denom) * 100);
            const prev = i > 0 ? steps[i - 1]!.count : base;
            const dropPct = prev > 0 ? Math.round((1 - s.count / prev) * 100) : 0;
            const barPct = Math.min(100, (s.count / denom) * 100);
            return (
              <li key={s.step}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-text-secondary">
                    {i + 1}. {ONB_STEP_LABELS[s.step] ?? s.step}
                  </span>
                  <span className="tabular text-text-primary">
                    {s.count.toLocaleString('de-DE')}{' '}
                    <span className="text-[11px] text-text-tertiary">({pctOfStart} %)</span>
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded bg-bg-elevated">
                  <div
                    className="h-full"
                    style={{ width: `${barPct}%`, background: darkTheme.colors.accent }}
                  />
                </div>
                {i > 0 && dropPct > 0 && (
                  <div className="mt-0.5 text-[10px]" style={{ color: darkTheme.colors.danger }}>
                    −{dropPct} % ggü. vorigem Schritt
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function BreakdownCard({
  title,
  items,
  labels,
}: {
  title: string;
  items: OnboardingLabeledCount[];
  labels: Record<string, string>;
}): JSX.Element {
  const total = items.reduce((acc, it) => acc + it.count, 0);
  const max = items.reduce((acc, it) => Math.max(acc, it.count), 0);
  const sorted = [...items].sort((a, b) => b.count - a.count);
  return (
    <div
      className="rounded-md border p-4"
      style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
    >
      <h3 className="mb-3 text-sm font-semibold text-text-primary">{title}</h3>
      {sorted.length === 0 && <p className="text-xs text-text-tertiary">Noch keine Daten.</p>}
      {sorted.length > 0 && (
        <ol className="space-y-2 text-sm">
          {sorted.map((it) => {
            const barPct = max > 0 ? (it.count / max) * 100 : 0;
            const share = total > 0 ? (it.count / total) * 100 : 0;
            return (
              <li key={it.key}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-text-secondary">{labels[it.key] ?? it.key}</span>
                  <span className="tabular text-text-primary">
                    {it.count.toLocaleString('de-DE')}{' '}
                    <span className="text-[11px] text-text-tertiary">({share.toFixed(0)} %)</span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-bg-elevated">
                  <div
                    className="h-full"
                    style={{ width: `${barPct}%`, background: darkTheme.colors.accent }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function ResumeMiniFunnel({
  shown,
  clicked,
}: {
  shown: number;
  clicked: number;
}): JSX.Element {
  const ctr = shown > 0 ? (clicked / shown) * 100 : 0;
  return (
    <div
      className="rounded-md border p-4"
      style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Wiedereinstieg (Deferred)</h3>
        <span className="text-[11px] text-text-tertiary">CTR {ctr.toFixed(0)} %</span>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wide text-text-tertiary">Karte gezeigt</div>
          <div className="tabular text-xl font-bold text-text-primary">
            {shown.toLocaleString('de-DE')}
          </div>
        </div>
        <span className="text-text-tertiary">→</span>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wide text-text-tertiary">Karte geklickt</div>
          <div className="tabular text-xl font-bold text-text-primary">
            {clicked.toLocaleString('de-DE')}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `vor ${Math.floor(ms / 1000)} s`;
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  return `vor ${Math.floor(ms / 3_600_000)} h`;
}
